import React, { useCallback } from 'react';

import noop from 'lodash/noop';
import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button/new';
import Form, { FormFooter, FormHeader, RequiredAsterisk } from '@atlaskit/form';

import { Text, Stack } from '@atlaskit/primitives/compiled';
import type { ValueType } from '@atlaskit/select';
import { useIntl } from '@atlassian/jira-intl';

import { useRouterActions } from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';
import type { SelectOption, StageConnectProps, StageProps } from '../../../../common/types.tsx';
import type { ApplicationLinkRemoteOAuth2ApplicationLink } from '../../../../common/entities.tsx';

import { putOAuth2ConfigInfo } from '../../../../rest/index.tsx';
import {
	APPLINKS_LIST_PAGE_URL,
	APPLINKS_LIST_PAGE_URL_LEGACY,
} from '../../../../common/constants.tsx';
import commonMessages from '../../../messages.tsx';
import messages from '../messages.tsx';
import CredentialsForm from '../../../edit/oauth2/credentials-form/CredentialsForm.tsx';

type ConnectSubpageProps = {
	state: StageConnectProps;
	onSuccess: (newState: StageProps) => void;
	onError: (error: string) => void;
	onCancel: () => void;
};

const ConnectSubpage = (props: ConnectSubpageProps) => {
	const { push } = useRouterActions();

	const { formatMessage } = useIntl();

	const getListPageUrl = useCallback(() => {
		if (fg('applinks-933-experimental-list-page')) {
			return APPLINKS_LIST_PAGE_URL;
		}
		return APPLINKS_LIST_PAGE_URL_LEGACY;
	}, []);

	const onFormSubmit = async (data: {
		remoteApplicationSupportedScopes?: ValueType<SelectOption, true>;
		applicationClientId: string;
		applicationClientSecret: string;
		redirectUrl?: string;
	}) => {
		const scopes = data.remoteApplicationSupportedScopes
			? data.remoteApplicationSupportedScopes.map((scope) => scope.value)
			: [];

		const oauth2Config: ApplicationLinkRemoteOAuth2ApplicationLink = {
			remoteClientId: data.applicationClientId,
			remoteClientSecret: data.applicationClientSecret,
			remoteRedirectUrl: data.redirectUrl,

			outboundScopes: scopes,
		};

		try {
			await putOAuth2ConfigInfo(props.state.applink.id, oauth2Config);
			push(getListPageUrl());
		} catch (error) {
			props.onError(
				error instanceof Error
					? error.message
					: formatMessage(commonMessages.unknownErrorTextNonfinal),
			);
			return error;
		}
	};

	return (
		<Form<{
			remoteApplicationSupportedScopes?: ValueType<SelectOption, true>;
			applicationClientId: string;
			applicationClientSecret: string;
			redirectUrl?: string;
			displayName: string;
		}>
			onSubmit={onFormSubmit}
		>
			{({ formProps, submitting }) => (
				<form {...formProps}>
					<Stack space="space.200">
						<FormHeader title={formatMessage(messages.connectHeaderNonfinal)}>
							<Stack space="space.200">
								<Text>{formatMessage(messages.connectDescriptionNonfinal)}</Text>
							</Stack>

							<Text aria-hidden="true" size="small">
								{formatMessage(messages.requiredFieldsTextNonfinal)} <RequiredAsterisk />
							</Text>
						</FormHeader>

						<CredentialsForm
							applink={props.state.applink}
							oauth2ConfigInfo={props.state.oauth2ConfigInfo}
							onDisplayError={props.onError}
							onRequestStatusRefresh={noop}
							partiallyLinkedMode
						/>

						<FormFooter>
							<ButtonGroup>
								<Button appearance="subtle" onClick={props.onCancel}>
									{formatMessage(messages.connectDoLaterButtonNonfinal)}
								</Button>
								<Button
									testId="applinks-administration.ui.list.creation-flow-dialog.connect.submit-button"
									type="submit"
									appearance="primary"
									isLoading={submitting}
								>
									{formatMessage(messages.connectContinueButtonNonfinal)}
								</Button>
							</ButtonGroup>
						</FormFooter>
					</Stack>
				</form>
			)}
		</Form>
	);
};

export default ConnectSubpage;
